/**
 * Masked Input Components
 * Using react-number-format PatternFormat for all masked inputs (avoids deprecated findDOMNode)
 */
import React, { forwardRef } from 'react';
import { NumericFormat, PatternFormat } from 'react-number-format';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { Minus, Plus } from 'lucide-react';

// CPF Input (000.000.000-00)
export const CpfInput = forwardRef(({ className, value, onChange, ...props }, ref) => (
  <PatternFormat
    format="###.###.###-##"
    mask="_"
    customInput={Input}
    getInputRef={ref}
    className={className}
    placeholder="000.000.000-00"
    value={value}
    onValueChange={(values) => {
      onChange?.({ target: { value: values.formattedValue } });
    }}
    {...props}
  />
));
CpfInput.displayName = 'CpfInput';

// CNPJ Input (00.000.000/0000-00)
export const CnpjInput = forwardRef(({ className, value, onChange, ...props }, ref) => (
  <PatternFormat
    format="##.###.###/####-##"
    mask="_"
    customInput={Input}
    getInputRef={ref}
    className={className}
    placeholder="00.000.000/0000-00"
    value={value}
    onValueChange={(values) => {
      onChange?.({ target: { value: values.formattedValue } });
    }}
    {...props}
  />
));
CnpjInput.displayName = 'CnpjInput';

// RG Input (00.000.000-0)
export const RgInput = forwardRef(({ className, value, onChange, ...props }, ref) => (
  <PatternFormat
    format="##.###.###-#"
    mask="_"
    customInput={Input}
    getInputRef={ref}
    className={className}
    placeholder="00.000.000-0"
    value={value}
    onValueChange={(values) => {
      onChange?.({ target: { value: values.formattedValue } });
    }}
    {...props}
  />
));
RgInput.displayName = 'RgInput';

// Phone Input (00) 00000-0000
export const PhoneInput = forwardRef(({ className, value, onChange, ...props }, ref) => {
  // Clean value to determine format
  const cleaned = value?.replace(/\D/g, '') || '';
  const format = cleaned.length > 10 ? '(##) #####-####' : '(##) ####-#####';
  
  return (
    <PatternFormat
      format={format}
      mask="_"
      customInput={Input}
      getInputRef={ref}
      className={className}
      placeholder="(00) 00000-0000"
      value={value}
      onValueChange={(values) => {
        onChange?.({ target: { value: values.formattedValue } });
      }}
      {...props}
    />
  );
});
PhoneInput.displayName = 'PhoneInput';

// CEP Input (00000-000)
export const CepInput = forwardRef(({ className, value, onChange, ...props }, ref) => (
  <PatternFormat
    format="#####-###"
    mask="_"
    customInput={Input}
    getInputRef={ref}
    className={className}
    placeholder="00000-000"
    value={value}
    onValueChange={(values) => {
      onChange?.({ target: { value: values.formattedValue } });
    }}
    {...props}
  />
));
CepInput.displayName = 'CepInput';

// Date Input (DD/MM/YYYY) - Selects all on focus
export const DateInput = forwardRef(({ className, value, onChange, onFocus, ...props }, ref) => {
  const handleFocus = (e) => {
    setTimeout(() => {
      e.target.select();
    }, 0);
    onFocus?.(e);
  };
  
  return (
    <PatternFormat
      format="##/##/####"
      mask="_"
      customInput={Input}
      getInputRef={ref}
      className={className}
      placeholder="DD/MM/AAAA"
      value={value}
      onFocus={handleFocus}
      onValueChange={(values) => {
        onChange?.({ target: { value: values.formattedValue } });
      }}
      {...props}
    />
  );
});
DateInput.displayName = 'DateInput';

// Time Input (HH:MM)
export const TimeInput = forwardRef(({ className, value, onChange, ...props }, ref) => (
  <PatternFormat
    format="##:##"
    mask="_"
    customInput={Input}
    getInputRef={ref}
    className={className}
    placeholder="00:00"
    value={value}
    onValueChange={(values) => {
      onChange?.({ target: { value: values.formattedValue } });
    }}
    {...props}
  />
));
TimeInput.displayName = 'TimeInput';

// Currency Input with configurable decimal places
// Supports both major (float) and minor (cents) modes
export const CurrencyInput = forwardRef(({ 
  className, 
  currency = 'BRL',
  decimalPlaces = 2,
  prefix,
  value,
  onValueChange,
  onFocus,
  useMinorUnits = false, // NEW: When true, value is in cents (1290 = R$ 12,90)
  ...props 
}, ref) => {
  const currencyPrefixes = {
    BRL: 'R$ ',
    USD: '$ ',
    EUR: '€ ',
  };
  
  const actualPrefix = prefix || currencyPrefixes[currency] || '';
  const multiplier = Math.pow(10, decimalPlaces);
  
  // Convert from minor to major for display
  const displayValue = useMinorUnits && value != null 
    ? value / multiplier 
    : value;
  
  // Handle focus to select all text for easy replacement
  const handleFocus = (e) => {
    setTimeout(() => {
      e.target.select();
    }, 0);
    onFocus?.(e);
  };
  
  const handleValueChange = (values) => {
    if (useMinorUnits) {
      // Convert major (float) to minor (cents)
      const minorValue = values.floatValue != null 
        ? Math.round(values.floatValue * multiplier) 
        : null;
      onValueChange?.(minorValue);
    } else {
      onValueChange?.(values.floatValue);
    }
  };
  
  return (
    <NumericFormat
      customInput={Input}
      getInputRef={ref}
      className={className}
      thousandSeparator={currency === 'BRL' ? '.' : ','}
      decimalSeparator={currency === 'BRL' ? ',' : '.'}
      decimalScale={decimalPlaces}
      fixedDecimalScale
      prefix={actualPrefix}
      allowNegative={false}
      value={displayValue}
      onFocus={handleFocus}
      onValueChange={handleValueChange}
      {...props}
    />
  );
});
CurrencyInput.displayName = 'CurrencyInput';

// Quantity Input (integers) with optional stepper buttons
export const QuantityInput = forwardRef(({ 
  className, 
  onValueChange,
  min = 0,
  max,
  value,
  step = 1,
  showButtons = false,
  onFocus,
  ...props 
}, ref) => {
  const [internalValue, setInternalValue] = React.useState(value ?? 0);
  
  React.useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);
  
  const handleFocus = (e) => {
    setTimeout(() => {
      e.target.select();
    }, 0);
    onFocus?.(e);
  };
  
  const handleChange = (newValue) => {
    let constrainedValue = newValue;
    if (min !== undefined && constrainedValue < min) constrainedValue = min;
    if (max !== undefined && constrainedValue > max) constrainedValue = max;
    setInternalValue(constrainedValue);
    onValueChange?.(constrainedValue);
  };
  
  const increment = () => handleChange((internalValue ?? 0) + step);
  const decrement = () => handleChange((internalValue ?? 0) - step);
  
  if (showButtons) {
    return (
      <div className={cn("flex items-center gap-1", className)}>
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={decrement}
          disabled={props.disabled || (min !== undefined && internalValue <= min)}
          className="h-9 w-9 shrink-0"
          tabIndex={-1}
        >
          <Minus className="h-4 w-4" />
        </Button>
        <NumericFormat
          customInput={Input}
          getInputRef={ref}
          className="text-center font-mono min-w-[60px]"
          decimalScale={0}
          allowNegative={min < 0}
          value={internalValue}
          onFocus={handleFocus}
          isAllowed={(values) => {
            const { floatValue } = values;
            if (floatValue === undefined) return true;
            if (min !== undefined && floatValue < min) return false;
            if (max !== undefined && floatValue > max) return false;
            return true;
          }}
          onValueChange={(values) => {
            setInternalValue(values.floatValue ?? 0);
            onValueChange?.(values.floatValue);
          }}
          {...props}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={increment}
          disabled={props.disabled || (max !== undefined && internalValue >= max)}
          className="h-9 w-9 shrink-0"
          tabIndex={-1}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    );
  }
  
  return (
    <NumericFormat
      customInput={Input}
      getInputRef={ref}
      className={className}
      decimalScale={0}
      allowNegative={min < 0}
      value={value}
      onFocus={handleFocus}
      isAllowed={(values) => {
        const { floatValue } = values;
        if (floatValue === undefined) return true;
        if (min !== undefined && floatValue < min) return false;
        if (max !== undefined && floatValue > max) return false;
        return true;
      }}
      onValueChange={(values) => {
        onValueChange?.(values.floatValue);
      }}
      {...props}
    />
  );
});
QuantityInput.displayName = 'QuantityInput';

// Percentage Input
export const PercentageInput = forwardRef(({ 
  className, 
  decimalPlaces = 2,
  onValueChange,
  value,
  ...props 
}, ref) => (
  <NumericFormat
    customInput={Input}
    getInputRef={ref}
    className={className}
    decimalScale={decimalPlaces}
    fixedDecimalScale
    suffix="%"
    allowNegative={false}
    value={value}
    isAllowed={(values) => {
      const { floatValue } = values;
      return floatValue === undefined || (floatValue >= 0 && floatValue <= 100);
    }}
    onValueChange={(values) => {
      onValueChange?.(values.floatValue);
    }}
    {...props}
  />
));
PercentageInput.displayName = 'PercentageInput';

export default {
  CpfInput,
  CnpjInput,
  RgInput,
  PhoneInput,
  CepInput,
  DateInput,
  TimeInput,
  CurrencyInput,
  QuantityInput,
  PercentageInput,
};
